import { bodyFont } from "./typographyService";
import { DOI_ICON_BASE64 } from "./orcid";
import { metadataService } from "./metadataService";

const PROPERTY_NAME = "libertatem.production.metadata";

export interface ProductionMetadata {
  journal: string;
  volume: string;
  issue: string;
  startPage: string;
  endPage: string;
  doi: string;
  publicationDate: string;
  section: string;
  sectionId: string;
  sectionAbbrev: string;
  licenseUrl: string;
  publisherName: string;
  publisherCountry: string;
  // OJS issue id this article will be published into (set by Create/Verify Issue).
  issueId: string;
  // OJS submission this paper is locked to, set by Contributors > Fetch from
  // OJS. Publish reuses it instead of asking for the id a second time.
  ojsSubmissionId: string;
  // Journal that production was last APPLIED for (header/footer/DOI written).
  // Locks Publish to this journal until production is updated or removed.
  appliedJournal: string;
  // Crossref Crossmark domain (publisher eTLD+1), derived from the journal
  // registry baseUrl at export time. Optional — only used for the PDF badge.
  crossmarkDomain?: string;
  // Whether the Crossmark badge should render in the PDF. Defaults to hidden
  // (opt-in) — the badge dialog is meaningless until the DOI is actually
  // registered with Crossref, so it stays off until the editor checks it.
  showCrossmark?: boolean;
}

export const EMPTY_PRODUCTION_METADATA: ProductionMetadata = {
  journal: "",
  volume: "",
  issue: "",
  startPage: "",
  endPage: "",
  doi: "",
  publicationDate: "",
  section: "",
  sectionId: "",
  sectionAbbrev: "",
  licenseUrl: "",
  publisherName: "",
  publisherCountry: "",
  issueId: "",
  ojsSubmissionId: "",
  appliedJournal: "",
  showCrossmark: false
};

export class ProductionService {
  async read(): Promise<ProductionMetadata> {
    return Word.run(async (context) => {
      const props = context.document.properties.customProperties;
      props.load("items/key,value");
      await context.sync();
      const prop = props.items.find((item) => item.key === PROPERTY_NAME);
      if (!prop) return { ...EMPTY_PRODUCTION_METADATA };
      try {
        return { ...EMPTY_PRODUCTION_METADATA, ...(JSON.parse(prop.value) as Partial<ProductionMetadata>) };
      } catch {
        return { ...EMPTY_PRODUCTION_METADATA };
      }
    });
  }

  async save(metadata: ProductionMetadata): Promise<void> {
    return Word.run(async (context) => {
      await this.queueSave(context, metadata);
      await context.sync();
    });
  }

  async apply(metadata: ProductionMetadata): Promise<void> {
    // Stamp the journal this document is now bound to. Publish locks to it.
    metadata = { ...metadata, appliedJournal: metadata.journal.trim() };
    return Word.run(async (context) => {
      await this.queueSave(context, metadata);
      const sections = context.document.sections;
      sections.load("items");
      await context.sync();

      const journalName = metadata.journal.trim() || "Libertatem";
      const targets = sections.items.length ? sections.items : [context.document.body.parentSection];
      const font = bodyFont();
      const headerOoxml = this.headerOoxml(metadata, journalName, font);
      const footerOoxml = this.footerOoxml(metadata, font);

      // Header and footer go in as complete OOXML. The per-paragraph APIs
      // (font/border/insertField) are unreliable on Mac Word and silently drop
      // pieces of the layout, so the centered multi-line text, the separator
      // rules, and the live PAGE field are all expressed as markup instead.
      for (const section of targets) {
        // "Replace" (not "Start"): inserting at Start leaves the header/footer's
        // default empty paragraph in place, and Word merges the last inserted
        // paragraph into it — which drops our centering onto that paragraph's
        // left-aligned default. Replacing the whole body avoids the merge.
        const header = section.getHeader("Primary");
        header.clear();
        header.insertOoxml(headerOoxml, "Replace");
        const footer = section.getFooter("Primary");
        footer.clear();
        footer.insertOoxml(footerOoxml, "Replace");
      }

      // Flush header/footer first so any failure here is reported as such, not
      // surfaced later under the DOI step.
      try {
        await context.sync();
      } catch (error) {
        throw new Error(`Apply Production: header/footer failed — ${error instanceof Error ? error.message : String(error)}`);
      }

      // DOI is optional. A DOI-block failure must NOT fail the whole Apply —
      // header/footer/metadata are already applied successfully.
      try {
        await this.applyDoiControl(context, metadata.doi.trim());
        await context.sync();
      } catch {
        // DOI block skipped (e.g. unsupported op); production is still applied.
      }
    });
  }

  // Strip everything Apply Production added: header + footer on every section,
  // the DOI content control (with its content), and the saved metadata property.
  async remove(): Promise<void> {
    return Word.run(async (context) => {
      const sections = context.document.sections;
      sections.load("items");
      await context.sync();
      const targets = sections.items.length ? sections.items : [context.document.body.parentSection];
      for (const section of targets) {
        section.getHeader("Primary").clear();
        section.getFooter("Primary").clear();
      }

      const controls = context.document.contentControls;
      controls.load("items/tag");
      await context.sync();
      for (const ctrl of controls.items) {
        if (metadataService.decode(ctrl.tag)?.type === "doi") ctrl.delete(false); // remove control + content
      }

      const props = context.document.properties.customProperties;
      props.load("items/key");
      await context.sync();
      const existing = props.items.find((item) => item.key === PROPERTY_NAME);
      if (existing) existing.delete();
      await context.sync();
    });
  }

  private async applyDoiControl(context: Word.RequestContext, doiText: string): Promise<void> {
    let cleanedDoi = doiText.trim();
    if (cleanedDoi.toLowerCase().startsWith("https://doi.org/")) {
      cleanedDoi = cleanedDoi.slice("https://doi.org/".length);
    } else if (cleanedDoi.toLowerCase().startsWith("http://doi.org/")) {
      cleanedDoi = cleanedDoi.slice("http://doi.org/".length);
    } else if (cleanedDoi.toLowerCase().startsWith("doi.org/")) {
      cleanedDoi = cleanedDoi.slice("doi.org/".length);
    }

    const controls = context.document.contentControls;
    controls.load("items/tag,title");
    await context.sync();

    let doiControl: Word.ContentControl | null = null;
    let lastAuthorControl: Word.ContentControl | null = null;
    let subtitleControl: Word.ContentControl | null = null;
    let titleControl: Word.ContentControl | null = null;
    let abstractControl: Word.ContentControl | null = null;

    for (const ctrl of controls.items) {
      try {
        const meta = metadataService.decode(ctrl.tag);
        if (meta) {
          if (meta.type === "doi") {
            doiControl = ctrl;
          } else if (meta.type === "author") {
            lastAuthorControl = ctrl;
          } else if (meta.type === "subtitle") {
            subtitleControl = ctrl;
          } else if (meta.type === "title") {
            titleControl = ctrl;
          } else if (meta.type === "abstract" || (meta.type === "section" && ctrl.title.toLowerCase().includes("abstract"))) {
            if (!abstractControl) abstractControl = ctrl;
          }
        }
      } catch {
        // ignore
      }
    }

    if (!cleanedDoi) {
      if (doiControl) {
        doiControl.delete(false); // keepContent=false -> remove control AND its content
      }
      return;
    }

    const doiUrl = `https://doi.org/${cleanedDoi}`;

    // Build the DOI content INTO the paragraph, then (for a new block) wrap it.
    // Inserting into the paragraph — not into the content-control range "Start"
    // — keeps the icon/text inside the control, so no leftover empty
    // "Click or tap here to enter text" placeholder control is created.
    let paragraph: Word.Paragraph;
    let wrapAfter = false;

    if (doiControl) {
      const content = doiControl.getRange("Content");
      content.clear();
      const innerParas = content.paragraphs;
      innerParas.load("items");
      await context.sync();
      paragraph = innerParas.items[0];
    } else {
      // Mac Word: ctrl.getRange("After") can land INSIDE the content control,
      // causing DOI to nest inside title/author. Instead, get the control's
      // last paragraph and insertParagraph("", "After") on IT — that reliably
      // lands outside the control boundary.
      const anchorCtrl = lastAuthorControl ?? subtitleControl ?? titleControl;
      if (anchorCtrl) {
        const ctrlParas = anchorCtrl.paragraphs;
        ctrlParas.load("items");
        await context.sync();
        const lastPara = ctrlParas.items[ctrlParas.items.length - 1];
        paragraph = lastPara.insertParagraph("", Word.InsertLocation.after);
      } else if (abstractControl) {
        const ctrlParas = abstractControl.paragraphs;
        ctrlParas.load("items");
        await context.sync();
        paragraph = ctrlParas.items[0].insertParagraph("", Word.InsertLocation.before);
      } else {
        paragraph = context.document.body.getRange("Start").insertParagraph("", Word.InsertLocation.after);
      }
      wrapAfter = true;
    }

    paragraph.alignment = "Centered";
    paragraph.font.name = bodyFont();
    paragraph.font.size = 11;
    paragraph.font.color = "black";
    paragraph.lineSpacing = 12;
    paragraph.spaceBefore = 6;
    paragraph.spaceAfter = 12;

    const img = paragraph.insertInlinePictureFromBase64(DOI_ICON_BASE64, Word.InsertLocation.start);
    img.hyperlink = doiUrl;

    const linkText = paragraph.insertText(` ${cleanedDoi}`, Word.InsertLocation.end);
    linkText.hyperlink = doiUrl;
    linkText.font.name = bodyFont();
    linkText.font.size = 11;
    linkText.font.color = "black";
    linkText.font.underline = "None";

    if (wrapAfter) {
      const control = paragraph.getRange().insertContentControl();
      control.title = "DOI";
      control.tag = metadataService.encode(metadataService.create("doi", { id: "doi" }));
      control.appearance = Word.ContentControlAppearance.boundingBox;
    }
  }

  private async queueSave(context: Word.RequestContext, metadata: ProductionMetadata): Promise<void> {
    const props = context.document.properties.customProperties;
    props.load("items/key,value");
    await context.sync();
    const existing = props.items.find((item) => item.key === PROPERTY_NAME);

    // Preserve the applied-journal lock across a plain Save (which may carry an
    // empty appliedJournal). Only apply() / remove() change the binding.
    let toSave = metadata;
    if (!metadata.appliedJournal && existing) {
      try {
        const prev = JSON.parse(existing.value) as Partial<ProductionMetadata>;
        if (prev.appliedJournal) toSave = { ...metadata, appliedJournal: prev.appliedJournal };
      } catch {
        // ignore malformed prior value
      }
    }

    const value = JSON.stringify(toSave);
    if (existing) {
      existing.value = value;
    } else {
      props.add(PROPERTY_NAME, value);
    }
  }

  // Volume/issue/date line shown beneath the journal name in the header.
  private headerMetaLine(metadata: ProductionMetadata): string {
    const bits = [
      metadata.volume.trim() && `Vol. ${metadata.volume.trim()}`,
      metadata.issue.trim() && `Issue ${metadata.issue.trim()}`,
      metadata.publicationDate.trim()
    ].filter(Boolean);
    return bits.join(" • ");
  }

  // ---------------------------------------------------------------------------
  // OOXML header/footer builders. Mac Word reliably renders OOXML inserts but
  // drops pieces of the equivalent per-paragraph API calls (custom fonts,
  // paragraph borders, inserted fields), so the whole header/footer layout is
  // expressed as markup. Sizes are half-points; spacing is twips (20 per point);
  // border w:sz is eighths of a point (4 = 0.5pt).
  // ---------------------------------------------------------------------------
  private xmlEscape(value: string): string {
    return value
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;");
  }

  private runProps(font: string, halfPt: number, opts: { bold?: boolean; color?: string } = {}): string {
    const safeFont = this.xmlEscape(font);
    return [
      "<w:rPr>",
      `<w:rFonts w:ascii="${safeFont}" w:hAnsi="${safeFont}" w:cs="${safeFont}"/>`,
      opts.bold ? "<w:b/>" : "",
      opts.color ? `<w:color w:val="${opts.color}"/>` : "",
      `<w:sz w:val="${halfPt}"/><w:szCs w:val="${halfPt}"/>`,
      "</w:rPr>"
    ].join("");
  }

  private runXml(text: string, font: string, halfPt: number, opts: { bold?: boolean; color?: string } = {}): string {
    return `<w:r>${this.runProps(font, halfPt, opts)}<w:t xml:space="preserve">${this.xmlEscape(text)}</w:t></w:r>`;
  }

  private paraXml(
    runsXml: string,
    opts: { spaceBefore?: number; spaceAfter?: number; rule?: "top" | "bottom" } = {}
  ): string {
    const before = Math.round((opts.spaceBefore ?? 0) * 20);
    const after = Math.round((opts.spaceAfter ?? 0) * 20);
    const ruleXml =
      opts.rule === "bottom"
        ? '<w:pBdr><w:bottom w:val="single" w:sz="4" w:space="3" w:color="CCCCCC"/></w:pBdr>'
        : opts.rule === "top"
          ? '<w:pBdr><w:top w:val="single" w:sz="4" w:space="3" w:color="CCCCCC"/></w:pBdr>'
          : "";
    // w:pPr children must follow the CT_PPr schema order: pBdr, then spacing,
    // then jc. Word silently drops an out-of-order w:jc (alignment is lost).
    return [
      "<w:p><w:pPr>",
      ruleXml,
      `<w:spacing w:before="${before}" w:after="${after}" w:line="240" w:lineRule="auto"/>`,
      '<w:jc w:val="center"/>',
      "</w:pPr>",
      runsXml,
      "</w:p>"
    ].join("");
  }

  private wrapOoxml(bodyParasXml: string): string {
    return [
      '<?xml version="1.0" encoding="UTF-8" standalone="yes"?>',
      '<pkg:package xmlns:pkg="http://schemas.microsoft.com/office/2006/xmlPackage">',
      '<pkg:part pkg:name="/_rels/.rels" pkg:contentType="application/vnd.openxmlformats-package.relationships+xml">',
      '<pkg:xmlData><Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships">',
      '<Relationship Id="rId1" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/officeDocument" Target="word/document.xml"/>',
      '</Relationships></pkg:xmlData></pkg:part>',
      '<pkg:part pkg:name="/word/document.xml" pkg:contentType="application/vnd.openxmlformats-officedocument.wordprocessingml.document.main+xml">',
      '<pkg:xmlData><w:document xmlns:w="http://schemas.openxmlformats.org/wordprocessingml/2006/main"><w:body>',
      bodyParasXml,
      "</w:body></w:document></pkg:xmlData></pkg:part>",
      "</pkg:package>"
    ].join("");
  }

  // Header: ~10px top margin, centered journal title, one blank line, centered
  // vol/issue/date meta, one blank line, a thin full-width rule.
  private headerOoxml(metadata: ProductionMetadata, journalName: string, font: string): string {
    const metaLine = this.headerMetaLine(metadata);
    let paras = this.paraXml(this.runXml(journalName, font, 20, { bold: true }), { spaceBefore: 8, spaceAfter: 0 });
    if (metaLine) {
      paras += this.paraXml(this.runXml(metaLine, font, 16, { color: "666666" }), { spaceBefore: 12, spaceAfter: 0 });
    }
    // Thin rule on its own line, one blank line below the meta. An empty
    // paragraph carries the bottom border, which draws full column width.
    paras += this.paraXml("", { spaceBefore: 12, rule: "bottom" });
    return this.wrapOoxml(paras);
  }

  // Footer: a thin rule above one centered inline line — "DOI: … • p. <folio>",
  // where <folio> is a live field that starts at the article's start page and
  // increments per page (12352, 12353, …), not Word's own 1, 2, 3.
  private footerOoxml(metadata: ProductionMetadata, font: string): string {
    const doi = metadata.doi.trim();
    let runs = "";
    if (doi) runs += this.runXml(`DOI: ${doi} • `, font, 16, { color: "666666" });
    runs += this.runXml("p. ", font, 16, { color: "666666" });
    runs += this.folioFieldXml(font, 16, metadata.startPage.trim());
    return this.wrapOoxml(this.paraXml(runs, { spaceAfter: 0, rule: "top" }));
  }

  // Live page folio. When startPage is numeric, emit a nested field
  // { = { PAGE } + (startPage-1) } so page 1 shows startPage and later pages
  // increment. Otherwise fall back to a plain PAGE field.
  private folioFieldXml(font: string, halfPt: number, startPage: string): string {
    const rpr = this.runProps(font, halfPt, { color: "666666" });
    const n = Number.parseInt(startPage, 10);
    if (!Number.isFinite(n)) {
      return `<w:fldSimple w:instr=" PAGE \\* MERGEFORMAT "><w:r>${rpr}<w:t>1</w:t></w:r></w:fldSimple>`;
    }
    const offset = n - 1;
    return [
      `<w:r>${rpr}<w:fldChar w:fldCharType="begin"/></w:r>`,
      `<w:r>${rpr}<w:instrText xml:space="preserve"> = </w:instrText></w:r>`,
      `<w:r>${rpr}<w:fldChar w:fldCharType="begin"/></w:r>`,
      `<w:r>${rpr}<w:instrText xml:space="preserve"> PAGE </w:instrText></w:r>`,
      `<w:r>${rpr}<w:fldChar w:fldCharType="end"/></w:r>`,
      `<w:r>${rpr}<w:instrText xml:space="preserve"> + ${offset} </w:instrText></w:r>`,
      `<w:r>${rpr}<w:fldChar w:fldCharType="separate"/></w:r>`,
      `<w:r>${rpr}<w:t>${n}</w:t></w:r>`,
      `<w:r>${rpr}<w:fldChar w:fldCharType="end"/></w:r>`
    ].join("");
  }
}

export const productionService = new ProductionService();
